import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { commentKeys } from '../comments/hooks';
import { setCommentMentions, updateCommentBody } from '../comments/queries';
import {
  deleteMessage,
  fetchRoomExtras,
  fetchRoomPins,
  fetchVisibleSources,
  forwardMessage,
  forwardWouldWiden,
  pinMessage,
  postThreadReply,
  unpinMessage,
} from './drop3c';

/**
 * Server state for Drop 3c on the Messages page. Its own file so the page
 * tests that mock ./hooks wholesale keep working; each of them mocks this
 * one too. The key root is written out rather than imported from ./hooks
 * for the same reason; it matches roomKeys.all so a room refresh covers it.
 */

const ROOMS_ROOT = ['rooms'] as const;

export const drop3cKeys = {
  extras: (roomId: string) => [...ROOMS_ROOT, '3c', 'extras', roomId] as const,
  pins: (roomId: string) => [...ROOMS_ROOT, '3c', 'pins', roomId] as const,
  sources: (ids: string[]) => [...ROOMS_ROOT, '3c', 'sources', ...ids] as const,
};

export function useRoomExtras(roomId: string) {
  const { client } = useAuth();
  return useQuery({
    queryKey: drop3cKeys.extras(roomId),
    queryFn: () => fetchRoomExtras(client, roomId),
    enabled: roomId !== '',
    retry: false,
  });
}

export function useVisibleSources(sourceIds: string[]) {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: [
      ...drop3cKeys.sources(sourceIds),
      access.status === 'approved' ? access.profile.id : null,
    ],
    queryFn: () => fetchVisibleSources(client, sourceIds),
    enabled: sourceIds.length > 0,
    refetchInterval: 15000,
    retry: false,
  });
}

export function useRoomPins(roomId: string) {
  const { client } = useAuth();
  return useQuery({
    queryKey: drop3cKeys.pins(roomId),
    queryFn: () => fetchRoomPins(client, roomId),
    enabled: roomId !== '',
    retry: false,
  });
}

/** Refresh everything the open room shows after a 3c write. */
function useRefreshRoom() {
  const queryClient = useQueryClient();
  return (roomId: string) => {
    void queryClient.invalidateQueries({ queryKey: commentKeys.thread(null, roomId) });
    void queryClient.invalidateQueries({ queryKey: drop3cKeys.extras(roomId) });
    void queryClient.invalidateQueries({ queryKey: drop3cKeys.pins(roomId) });
    void queryClient.invalidateQueries({ queryKey: ROOMS_ROOT });
  };
}

export function useTogglePin(roomId: string) {
  const { client } = useAuth();
  const refresh = useRefreshRoom();
  return useMutation({
    mutationFn: (input: { commentId: string; pinned: boolean }) =>
      input.pinned
        ? unpinMessage(client, input.commentId)
        : pinMessage(client, { commentId: input.commentId, roomId }),
    onSettled: () => refresh(roomId),
  });
}

export function usePostThreadReply(roomId: string) {
  const { client, access } = useAuth();
  const authorId = access.status === 'approved' ? access.profile.id : '';
  const refresh = useRefreshRoom();
  return useMutation({
    mutationFn: (input: { parentCommentId: string; body: string; mentionedUserIds: string[] }) =>
      postThreadReply(client, { ...input, roomId, authorId }),
    onSuccess: () => refresh(roomId),
  });
}

export function useForwardWouldWiden() {
  const { client } = useAuth();
  return useMutation({
    mutationFn: (input: { sourceRoomId: string; targetRoomId: string }) =>
      forwardWouldWiden(client, input),
  });
}

export function useForwardMessage() {
  const { client } = useAuth();
  const refresh = useRefreshRoom();
  return useMutation({
    mutationFn: (input: { sourceCommentId: string; targetRoomId: string; note: string }) =>
      forwardMessage(client, input),
    onSuccess: (_id, input) => refresh(input.targetRoomId),
  });
}

export function useDeleteMessage(roomId: string) {
  const { client } = useAuth();
  const refresh = useRefreshRoom();
  return useMutation({
    mutationFn: (commentId: string) => deleteMessage(client, commentId),
    onSettled: () => refresh(roomId),
  });
}

/**
 * Edit your own message: the body (the one column the browser may update,
 * the edited_at stamp is the trigger's), then the mention rows, added and
 * removed only (setCommentMentions). Not useUpdateComment: that one refreshes
 * the thread only, and Messages also shows the text in the room list preview
 * and the pinned strip, which useRefreshRoom covers.
 */
export function useEditMessage(roomId: string) {
  const { client } = useAuth();
  const refresh = useRefreshRoom();
  return useMutation({
    mutationFn: async (input: { commentId: string; body: string; mentionedUserIds: string[] }) => {
      await updateCommentBody(client, input.commentId, input.body);
      await setCommentMentions(client, {
        commentId: input.commentId,
        projectId: roomId,
        mentionedUserIds: input.mentionedUserIds,
      });
    },
    onSettled: () => refresh(roomId),
  });
}
